import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { languageChoiceOf } from '../../../i18n/languages';
import type { LanguageChoice } from '../../../i18n/languages';
import { languageViewOf, profileQueryOptions, useLanguageChoice } from '../../account';
import type { LanguageChoiceLogic, LanguageNote } from '../../account';
import { mySettingsQueryOptions } from '../../close-the-loop';
import type { MySettings, SettingsLanguage } from '../../close-the-loop';

export interface OnboardingLanguage {
  readonly shown: LanguageChoice;
  readonly options: readonly LanguageChoice[];
  readonly note: LanguageNote;
  readonly status: LanguageChoiceLogic['status'];
  readonly choose: (next: LanguageChoice) => void;
  readonly continueWith: (onDone: () => void) => void;
}

const languageOf = (settings: MySettings): SettingsLanguage | null => settings.language;

export const useOnboardingLanguage = (): OnboardingLanguage => {
  const { i18n } = useTranslation();
  const { data: profile } = useQuery(profileQueryOptions());
  const { data: resolved } = useQuery({ ...mySettingsQueryOptions(), select: languageOf });
  const view = languageViewOf(languageChoiceOf(i18n.language), resolved ?? null);
  const language = useLanguageChoice({ current: view.current, timing: 'instant' });
  const saving = language.status === 'saving' || language.status === 'paused';
  const kept = resolved ? resolved.effective : profile?.language;

  return {
    shown: language.shown,
    options: view.options,
    note: view.note,
    status: language.status,
    choose: (next) => language.choose(next),
    continueWith: (onDone) => {
      if (saving) return;
      if (language.shown === kept) onDone();
      else language.choose(language.shown, onDone);
    },
  };
};
